import type { CmsEditingContext } from '@eneco/cms-adapter';

/**
 * Builds the `data-sanity` factory the render host hands to `CmsProvider`.
 *
 * Bound once per page to the document the page came from, so the render tree only has to
 * supply the field path it already carries (`CmsRendering.fieldPath`,
 * `CmsRendering.placeholderPaths`). Keeping the binding here is what lets `@eneco/cms`
 * stay provider-neutral — it receives a plain `(path, document?) => string | undefined`.
 *
 * ## Why this is JSON and not `createDataAttribute`
 *
 * `createDataAttribute` writes `id=…;type=…;path=sections:KEY.field`, and the overlay reads that back
 * with `urlStringToPath` (`@sanity/visual-editing-csm`), whose key pattern is `/^([\w-]+):([\w-]+)$/`.
 * That admits letters, digits, `_` and `-` — and nothing else. Every `_key` in a Sitecore-migrated
 * document is a GUID wrapped in braces, `{7C4810BD-27AC-440F-B20A-53C700B8DED1}`, so the segment does
 * not match, is pushed whole as if it were a field name, and the path decodes to a field called
 * `sections:{7C48…}` that the document does not have. The Studio cannot resolve it, and the editor is
 * left on the `sections` overview instead of the block or the field they clicked.
 *
 * It is silent, and it is the reason a click on any element marked here went nowhere useful while a
 * click on text worked: stega carries the path as structured segments, never through that regex.
 *
 * `decodeSanityNodeData` tries `JSON.parse` first and, given an object, keeps `path` exactly as
 * written — a Studio path (`sections[_key=="{7C48…}"].field`), which is what every path in this lane
 * already is. So the node is emitted as JSON and the fragile string form is never involved. The test
 * decodes what this produces with the overlay's own decoder, braced key included.
 */
export const createEditAttribute = (
  editing: CmsEditingContext,
): ((path: string, document?: { id: string; type: string }) => string) => {
  const node = (id: string, type: string, path: string): string =>
    JSON.stringify({
      id,
      type,
      path,
      baseUrl: editing.studioUrl,
      // Omitting this makes the edit URL assume the `default` workspace and drop the segment, which
      // a Studio with named workspaces answers with "Studio workspace not found". `undefined` is
      // dropped by `JSON.stringify`, so an unset workspace is simply absent rather than invented.
      workspace: editing.workspace,
    });

  // A field of a document the page merely inlined (`siteWideContent->{ … }`) names that document,
  // not the page's: the same path in the page document would point at a reference.
  return (path, document) =>
    document ? node(document.id, document.type, path) : node(editing.documentId, editing.documentType, path);
};
